/**
 * ErrorsWrapper.tsx
 *
 * This React component displays a styled message box for various types of messages:
 * error, warning, info, or default. It is intended to provide a consistent UI
 * for error or feedback messages throughout the app.
 *
 * Features:
 * - Supports multiple variants: error, warning, info, and default
 * - Reusable across components for error or status display
 * - Tailwind-based styling
 *
 * Props:
 * - `message` (string): The message text to display
 * - `variant` (optional string): One of "error" | "warning" | "info" | "default" (default: "error")
 *
 * Dependencies:
 * - Type: ErrorMessageProps from GeneralComponents types
 *
 * Author: DomeCare Dev Team
 * Created: 2025-06-02
 */

import React from "react";
import { ErrorMessageProps } from "../../types/GeneralComponents";

// -------------------------
// Component: ErrorsWrapper
// -------------------------

const ErrorsWrapper: React.FC<ErrorMessageProps> = ({
  message,
  variant = "error",
}) => {
  // -------------------------
  // Style Logic Based on Variant
  // -------------------------

  const getVariantStyles = () => {
    switch (variant) {
      case "error":
        return "text-errorMain";
      case "warning":
        return "bg-yellow-100 text-yellow-700 border-yellow-400";
      case "info":
        return "bg-blue-100 text-blue-700 border-blue-400";
      default:
        return "bg-gray-100 text-gray-700 border-gray-400";
    }
  };

  // -------------------------
  // Render
  // -------------------------

  return (
    <div className={`rounded p-4 ${getVariantStyles()}`}>
      <p className="text-sm font-semibold">{message}</p>
    </div>
  );
};

export default ErrorsWrapper;
